// src/app/[lang]/layouts/BaseLayout.tsx
"use client";
import { Suspense, ReactNode, useMemo } from 'react';
import LoadingFallback from '../components/LoadingFallback';
import ErrorBoundary from '../components/ErrorBoundary';
import ArticleSidebar from '../components/ArticleSidebar';
import {
  GetGlobalSettingsQuery,
  GetJournalIssuesQuery,
  GetBooksQuery,
} from '@/gql/gql-generated';

interface BaseLayoutProps {
  globalSettings?: GetGlobalSettingsQuery['globalSettings'];
  leftSidebar: ReactNode;
  mainContent: ReactNode;
  rightSidebar?: ReactNode;
  slug?: string;
  isLoading?: boolean;
  lang: string;
  journalIssues: GetJournalIssuesQuery;
  books?: GetBooksQuery;
  pageType?: 'article' | 'home' | 'book' | 'other';
  context?: string;
}

// Memoized sticky sidebar styles to prevent recalculation
const sidebarStyles = {
  paddingRight: '10px',
} as const;

const BaseLayout = ({
  leftSidebar,
  mainContent,
  rightSidebar,
  isLoading,
  lang,
  journalIssues,
    books,
  pageType,
  context,
}: BaseLayoutProps) => {
  // MOVE ALL HOOKS TO THE TOP
  const hasRightSidebar = !!rightSidebar;

  const layoutWidths = useMemo(
    () => ({
      leftSidebarWidth: hasRightSidebar ? 'lg:w-[20%]' : 'lg:w-[30%]',
      mainContentWidth: hasRightSidebar ? 'lg:w-[60%]' : 'lg:w-[70%]',
    }),
    [hasRightSidebar]
  );

  if (isLoading) {
    return <LoadingFallback variant="default" />;
  }

  return (
    <ErrorBoundary>
      {/* JUST THE 3-COLUMN LAYOUT - NO CHROME */}
      {/* An article that is in no journal and no book gets an empty left aside (ArticleSidebar
          renders null). The layout is a cached shell that cannot know that, so CSS does:
          when the aside's inner div has no children, hide it and centre the reading column. */}
      <div className="flex flex-col lg:flex-row lg:justify-between mt-0 lg:mt-5 print:mt-4 print:w-full overflow-visible lg:[&:has(>aside>div:empty)]:justify-center lg:[&:has(>aside>div:empty)>aside]:hidden">
        {/* Left Sidebar */}
        <aside
          className={`hidden lg:block w-full ${layoutWidths.leftSidebarWidth} print:hidden overflow-visible`}
          aria-label="Left Sidebar"
        >
          <div
            className="sticky custom-scrollbar lg:top-8 overflow-y-auto overflow-x-visible max-h-[92vh] mr-10 overflow-visible"
            style={sidebarStyles}
          >
            <ErrorBoundary>
              {/* Conditional rendering based on page type */}
              {pageType === 'article' ? (
                // Suspense because ArticleSidebar reads ?context= via useSearchParams.
                  <Suspense fallback={null}>
                    <ArticleSidebar
                      lang={lang}
                      journalIssues={journalIssues}
                      books={books}
                    />
                  </Suspense>
              ) : (
                leftSidebar
              )}
            </ErrorBoundary>
          </div>
        </aside>

        {/* Main Content */}
        <main
          id="main-content"
          className={`w-full ${layoutWidths.mainContentWidth} bg-custom-bg mb-4 lg:mt-0 print:mt-4 print:bg-white overflow-visible`}
        >
          <ErrorBoundary>{mainContent}</ErrorBoundary>
          {/* Phones cannot see the aside above, so the issue / book contents follow the article */}
          {pageType === 'article' && (
            <div className="lg:hidden mt-6 print:hidden">
              <ErrorBoundary>
                <Suspense fallback={null}>
                  <ArticleSidebar
                    lang={lang}
                    journalIssues={journalIssues}
                    books={books}
                    collapsible
                  />
                </Suspense>
              </ErrorBoundary>
            </div>
          )}
        </main>

        {/* Right Sidebar - Conditionally rendered */}
        {hasRightSidebar && (
          <aside
            className="hidden lg:block w-full lg:w-[20%] print:hidden overflow-visible"
            aria-label="Right Sidebar"
          >
            <div className="sticky custom-scrollbar lg:top-8 overflow-y-auto overflow-x-visible max-h-[95vh] ml-4 overflow-visible">
              <ErrorBoundary>{rightSidebar}</ErrorBoundary>
            </div>
          </aside>
        )}
      </div>
    </ErrorBoundary>
  );
};

export default BaseLayout;